import React, { useContext } from 'react';
import { isSameDay } from 'date-fns';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Message from 'components/Message';
import Pill from 'components/Pill';
import { getTodayInVenueTimezone } from 'features/Schedules/ScheduleCalendar/ScheduleCalendar.helpers';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';
import { selectActiveVenueTimezone, selectSchedulesStartDate } from 'selectors/general';

import VenueSchedulesContext from './Contexts/VenueSchedulesContext';
import { findNextUpcomingSchedule } from './Contexts/VenueSchedules.helpers';
import { setSelectedSchedule, updateStartDate } from './YieldManagement.saga';

import './ScheduleTodayButton.scss';

const { block } = bem('schedule-today-button');

const messages = defineMessages({
  today: {
    id: 'yield-management.schedule-today-button.label',
    defaultMessage: 'Today',
  },
});

const ScheduleTodayButton = ({ venueId }) => {
  const dispatch = useDispatch();
  const startDate = useSelector(selectSchedulesStartDate);
  const venueTimezone = useSelector(selectActiveVenueTimezone);
  const { requestNextUpcomingSelection, schedules } = useContext(VenueSchedulesContext);

  const selectNextUpcomingSchedule = () => {
    const nextSchedule = findNextUpcomingSchedule(schedules, venueTimezone);

    if (nextSchedule) {
      dispatch(setSelectedSchedule({ scheduleId: nextSchedule.scheduleId }));
    }
  };

  const handleClick = () => {
    Segment.event('Click Today Filter', { venueId });

    const today = getTodayInVenueTimezone(venueTimezone);

    // Changing the date reloads the list asynchronously, so the selection has to wait for the new
    // schedules; when today is already selected no reload happens and the loaded list is final.
    if (startDate && isSameDay(startDate, today)) {
      selectNextUpcomingSchedule();
      return;
    }

    requestNextUpcomingSelection();
    dispatch(updateStartDate(today));
  };

  return (
    <Message
      as={Pill}
      className={block()}
      tagName="button"
      type="button"
      theme="light"
      message={messages.today}
      onClick={handleClick}
      data-cypress="schedule-today-button"
    />
  );
};

ScheduleTodayButton.propTypes = {
  venueId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ScheduleTodayButton;
